import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { CombineLatestSubscriber } from 'rxjs/internal/observable/combineLatest';
import {
    filter,
    map,
    takeWhile,
    withLatestFrom,
    distinctUntilChanged,
    take,
    delay
} from 'rxjs/operators';
import { EmployeeModel } from '../../../models';
import { MessageService, SelectItem } from '../../../shared';
import { LoggingService } from '../../../shared/services/logging';
import * as fromShared from '../../../shared';
import * as fromSettings from '../../../company-setting';
import {
    GetContractorsWithHours,
    GetEmployeesWithHours,
    GetMaxApprovedTimeNotSent,
    GetMinApprovedTimeNotSent,
    SendBills,
    SendHours,
    SetEmployeesWithHours
} from '../../store/quickbooks.actions';
import {
    getEmployeesWithHours,
    getContractorsWithHours,
    getMinApprovedTimeNotSent,
    getMaxApprovedTimeNotSent,
    getHasValidTokens
} from '../../store/quickbooks.selectors';

@Component({
    selector: 'ef-send-hours-container',
    templateUrl: './send-hours-container.component.html',
    styleUrls: ['./send-hours-container.component.scss'],
    standalone: false
})
export class SendHoursContainerComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    process$: Observable<fromShared.Process>;
    public sendDoctoQB$: Observable<boolean>;
    public employeesWithHours$: Observable<SelectItem[] | undefined>;
    public contractorsWithHours$: Observable<SelectItem[] | undefined>;
    public startDate$: Observable<Date | null>;
    public endDate$: Observable<Date | null>;
    private active = true;
    private filterSubject = new ReplaySubject<{
        StartDate: Date;
        EndDate: Date;
        Resend: boolean;
    }>(1);
    private sendHoursSubject = new ReplaySubject<SelectItem[]>(1);
    private sendBillsSubject = new ReplaySubject<SelectItem[]>(1);

    // Add this property to track token state
    private hasValidTokens$ = this.store.pipe(
        select(getHasValidTokens),
        distinctUntilChanged()
    );

    constructor(
        private store: Store,
        private log: LoggingService,
        private messageService: MessageService
    ) {}
    ngOnDestroy(): void {
        this.active = false;
    }
    ngOnInit(): void {
        this.process$ = this.store.pipe(select(fromShared.selectCurrent));
        this.setupSubscriptions();
        this.store.dispatch(new SetEmployeesWithHours([])); //clear cache
        this.store.dispatch(new GetMinApprovedTimeNotSent());
        this.store.dispatch(new GetMaxApprovedTimeNotSent());
        this.store.dispatch(
            fromSettings.getCompanySetting({
                SettingType: 'SendDoctoQB'
            })
        );
    }
    ngAfterViewInit(): void {}
    setupSubscriptions(): void {
        this.sendDoctoQB$ = this.store.pipe(select(fromSettings.SendDoctoQB));
        this.employeesWithHours$ = this.store.pipe(
            select(getEmployeesWithHours),
            map(items =>
                items?.map(item => ({
                    label: item.EmployeeName,
                    value: item.EmployeeListID
                }))
            )
        );
        this.contractorsWithHours$ = this.store.pipe(
            select(getContractorsWithHours),
            map(items =>
                items?.map(item => ({
                    label: item.EmployeeName,
                    value: item.EmployeeListID
                }))
            )
        );
        this.filterSubject
            .pipe(takeWhile(() => this.active))
            .subscribe(value => {
                this.store.dispatch(new GetEmployeesWithHours(value));
                this.store.dispatch(new GetContractorsWithHours(value));
            });
        this.sendHoursSubject
            .pipe(
                takeWhile(() => this.active),
                withLatestFrom(this.filterSubject, this.hasValidTokens$)
            )
            .subscribe(([items, filter, hasValidTokens]) => {
                // Only proceed if we have valid tokens
                if (!hasValidTokens) {
                    this.log.verbose({
                        type: 'SendHoursContainerComponent:sendHours',
                        message:
                            'Cannot send hours: QuickBooks tokens are invalid'
                    });

                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Cannot send hours: QuickBooks authorization required'
                    });
                    return;
                }

                // Process items one at a time instead of all at once
                const sendNextItem = (index = 0) => {
                    if (index >= items.length) return;

                    this.store.dispatch(
                        new SendHours({
                            EmployeeListID: items[index].value,
                            startDate: filter.StartDate,
                            endDate: filter.EndDate,
                            resend: filter.Resend
                        })
                    );

                    // Wait and check if tokens are still valid before sending next item
                    const subscription = this.hasValidTokens$
                        .pipe(take(1), delay(500))
                        .subscribe(stillValid => {
                            if (stillValid) {
                                sendNextItem(index + 1);
                            } else {
                                this.log.verbose({
                                    type: 'SendHoursContainerComponent:sendHours',
                                    message:
                                        'Stopped sending hours due to token validation failure',
                                    processedItems: index + 1,
                                    totalItems: items.length
                                });
                            }
                            subscription.unsubscribe();
                        });
                };

                sendNextItem();
            });
        this.sendBillsSubject
            .pipe(
                takeWhile(() => this.active),
                withLatestFrom(this.filterSubject, this.hasValidTokens$)
            )
            .subscribe(([items, filter, hasValidTokens]) => {
                // Only proceed if we have valid tokens
                if (!hasValidTokens) {
                    this.log.verbose({
                        type: 'SendHoursContainerComponent:sendBills',
                        message: 'Cannot send bills: QuickBooks tokens are invalid'
                    });

                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Cannot send bills: QuickBooks authorization required'
                    });
                    return;
                }

                // Process items one at a time instead of all at once
                const sendNextItem = (index = 0) => {
                    if (index >= items.length) return;

                    this.store.dispatch(
                        new SendBills({
                            EmployeeListID: items[index].value,
                            startDate: filter.StartDate,
                            endDate: filter.EndDate,
                            resend: filter.Resend
                        })
                    );

                    // Wait and check if tokens are still valid before sending next item
                    const subscription = this.hasValidTokens$
                        .pipe(take(1), delay(500))
                        .subscribe(stillValid => {
                            if (stillValid) {
                                sendNextItem(index + 1);
                            } else {
                                this.log.verbose({
                                    type: 'SendHoursContainerComponent:sendBills',
                                    message: 'Stopped sending bills due to token validation failure',
                                    processedItems: index + 1,
                                    totalItems: items.length
                                });
                            }
                            subscription.unsubscribe();
                        });
                };

                sendNextItem();
            });
        this.endDate$ = this.store.pipe(
            select(getMaxApprovedTimeNotSent),
            filter(x => !!x)
        );
        this.startDate$ = this.store.pipe(
            select(getMinApprovedTimeNotSent),
            filter(x => !!x)
        );
    }

    onFilter(value: {
        StartDate: Date;
        EndDate: Date;
        Resend: boolean;
        SendDocumentation: boolean;
    }) {
        this.log.verbose({
            type: 'SendHoursContainerComponent:onFilter',
            value
        });
        this.filterSubject.next(value);
    }
    onSendHours(value: SelectItem[]) {
        this.log.verbose({
            type: 'SendHoursContainerComponent:onSendHours',
            value
        });
        this.sendHoursSubject.next(value);
    }
    onSendBills(value: SelectItem[]) {
        this.log.verbose({
            type: 'SendHoursContainerComponent:onSendHours',
            value
        });
        this.sendBillsSubject.next(value);
    }
    onSetSendDocumentation(value: boolean) {
        this.log.verbose({
            type: 'SendHoursContainerComponent:onSendDocumentation',
            value
        });
        this.store.dispatch(
            fromSettings.putCompanySettings({
                settings: [
                    {
                        SettingType: 'SendDoctoQB',
                        SettingValue: value === true ? '1' : '0'
                    }
                ]
            })
        );
    }
}
